import type { CollectionItem, UserAlert } from '../types/index';

const WATCHLIST_KEY = 'appstatsview_watchlist';
const COLLECTIONS_KEY = 'appstatsview_collections';
const ALERTS_KEY = 'appstatsview_alerts';
const USER_KEY = 'appstatsview_user';

export const StorageService = {
  getWatchlist(): string[] {
    try {
      const data = localStorage.getItem(WATCHLIST_KEY);
      return data ? JSON.parse(data) : ['duolingo', 'chatgpt', 'notion'];
    } catch {
      return ['duolingo', 'chatgpt', 'notion'];
    }
  },

  toggleWatchlist(appId: string): boolean {
    const list = this.getWatchlist();
    let updated: string[];
    let added = false;
    if (list.includes(appId)) {
      updated = list.filter(id => id !== appId);
    } else {
      updated = [...list, appId];
      added = true;
    }
    try {
      localStorage.setItem(WATCHLIST_KEY, JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('appstatsview_watchlist_updated', { detail: updated }));
    } catch (e) {
      console.error(e);
    }
    return added;
  },

  isWatchlisted(appId: string): boolean {
    return this.getWatchlist().includes(appId);
  },

  getCollections(): CollectionItem[] {
    try {
      const data = localStorage.getItem(COLLECTIONS_KEY);
      if (data) return JSON.parse(data);
    } catch {
      // fallback
    }
    const defaults: CollectionItem[] = [
      {
        id: 'col-1',
        name: 'AI Apps & Utilities',
        description: 'Tracking high velocity generative AI applications and smart assistants.',
        appIds: ['chatgpt', 'notion', 'canva'],
        createdAt: '2026-08-01',
        updatedAt: '2026-08-24',
        color: '#1177ff'
      },
      {
        id: 'col-2',
        name: 'Language Education Leaders',
        description: 'Language learning apps competitive landscape.',
        appIds: ['duolingo', 'babbel', 'busuu', 'memrise', 'rosetta-stone'],
        createdAt: '2026-08-10',
        updatedAt: '2026-08-23',
        color: '#16a66a'
      },
      {
        id: 'col-3',
        name: 'Breakout Watchlist',
        description: 'Apps showing extreme outlier growth and rapid chart velocity.',
        appIds: ['duolingo', 'chatgpt', 'capcut', 'notion'],
        createdAt: '2026-08-15',
        updatedAt: '2026-08-25',
        color: '#8b5cf6'
      }
    ];
    this.saveCollections(defaults);
    return defaults;
  },

  saveCollections(collections: CollectionItem[]) {
    try {
      localStorage.setItem(COLLECTIONS_KEY, JSON.stringify(collections));
      window.dispatchEvent(new CustomEvent('appstatsview_collections_updated', { detail: collections }));
    } catch (e) {
      console.error(e);
    }
  },

  addAppToCollection(collectionId: string, appId: string) {
    const list = this.getCollections();
    const target = list.find(c => c.id === collectionId);
    if (target && !target.appIds.includes(appId)) {
      target.appIds.push(appId);
      target.updatedAt = new Date().toISOString().split('T')[0];
      this.saveCollections(list);
    }
  },

  createCollection(name: string, description: string, initialAppIds: string[] = []): CollectionItem {
    const list = this.getCollections();
    const newItem: CollectionItem = {
      id: `col-${Date.now()}`,
      name,
      description,
      appIds: initialAppIds,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
      color: '#1177ff'
    };
    list.unshift(newItem);
    this.saveCollections(list);
    return newItem;
  },

  getAlerts(): UserAlert[] {
    try {
      const data = localStorage.getItem(ALERTS_KEY);
      if (data) return JSON.parse(data);
    } catch {
      // fallback
    }
    const sampleAlerts: UserAlert[] = [
      {
        id: 'alt-1',
        appId: 'duolingo',
        appName: 'Duolingo',
        type: 'ranking',
        message: 'Duolingo reached #1 in Mexico Education category.',
        timestamp: '15m ago',
        read: false
      },
      {
        id: 'alt-2',
        appId: 'chatgpt',
        appName: 'ChatGPT',
        type: 'breakout',
        message: 'Extreme Outlier Trigger: 314% growth above 30-day baseline.',
        timestamp: '2h ago',
        read: false
      },
      {
        id: 'alt-3',
        appId: 'notion',
        appName: 'Notion',
        type: 'growth',
        message: 'Notion surged +6 positions in Germany charts.',
        timestamp: '1d ago',
        read: true
      }
    ];
    this.saveAlerts(sampleAlerts);
    return sampleAlerts;
  },

  saveAlerts(alerts: UserAlert[]) {
    try {
      localStorage.setItem(ALERTS_KEY, JSON.stringify(alerts));
      window.dispatchEvent(new CustomEvent('appstatsview_alerts_updated', { detail: alerts }));
    } catch (e) {
      console.error(e);
    }
  },

  addAlert(alert: Partial<UserAlert> & { appId: string; appName: string }): UserAlert {
    const list = this.getAlerts();
    const newAlert: UserAlert = {
      id: `alt-${Date.now()}`,
      appId: alert.appId,
      appName: alert.appName,
      type: alert.type || 'growth',
      message: alert.message || `Alert configured for ${alert.appName}`,
      timestamp: 'Just now',
      read: false
    };
    list.unshift(newAlert);
    this.saveAlerts(list);
    return newAlert;
  },

  removeAlert(alertId: string) {
    const list = this.getAlerts().filter(a => a.id !== alertId);
    this.saveAlerts(list);
  },

  markAlertsAsRead() {
    const list = this.getAlerts().map(a => ({ ...a, read: true }));
    this.saveAlerts(list);
  },

  getUser() {
    try {
      const u = localStorage.getItem(USER_KEY);
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  },

  setUser(user: { name: string; email: string; plan: 'Free' | 'Pro' | 'Business' } | null) {
    try {
      if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
      else localStorage.removeItem(USER_KEY);
      window.dispatchEvent(new CustomEvent('appstatsview_user_updated', { detail: user }));
    } catch (e) {
      console.error(e);
    }
  }
};
